import {
  deleteProductUpdate as deleteProductUpdateRequest,
  deleteProductUpdateImage as deleteProductUpdateImageRequest,
  getProductUpdatesForUser as getProductUpdatesForUserRequest,
  listAllProductUpdates as listAllProductUpdatesRequest,
  uploadProductUpdateImage as uploadProductUpdateImageRequest,
  upsertProductUpdate as upsertProductUpdateRequest,
} from './api';
import type {
  ProductUpdateData,
  ProductUpdateImageResponse,
  ProductUpdateManagementResponse,
  ProductUpdatesListResponse,
  ProductUpdatesManagementListResponse,
  ProductUpdateUpdate,
} from './api';
import { getTodayInPT } from '../utils/dateUtils';

export type {
  ProductUpdateData,
  ProductUpdateImageResponse,
  ProductUpdateManagementResponse,
  ProductUpdatesListResponse,
  ProductUpdatesManagementListResponse,
  ProductUpdateUpdate,
};

export async function getProductUpdatesForUser(): Promise<ProductUpdatesListResponse> {
  const res = await getProductUpdatesForUserRequest({
    throwOnError: true,
  });
  if (!res.data) {
    throw new Error('Invalid product updates response');
  }
  return res.data;
}

/** True when expires_at is on or before today (Pacific); null never expires. */
export function isProductUpdateExpired(
  expiresAt: string | null | undefined
): boolean {
  if (!expiresAt) {
    return false;
  }
  return expiresAt <= getTodayInPT();
}

export async function listAllProductUpdates(): Promise<ProductUpdatesManagementListResponse> {
  const res = await listAllProductUpdatesRequest({
    throwOnError: true,
  });
  if (!res.data) {
    throw new Error('Invalid product updates list response');
  }
  return res.data;
}

export async function upsertProductUpdate(
  body: ProductUpdateUpdate
): Promise<ProductUpdateManagementResponse> {
  const res = await upsertProductUpdateRequest({
    body,
    throwOnError: true,
  });
  if (!res.data) {
    throw new Error('Invalid product update response');
  }
  return res.data;
}

export async function deleteProductUpdate(
  productUpdateId: string
): Promise<void> {
  await deleteProductUpdateRequest({
    path: { product_update_id: productUpdateId },
    throwOnError: true,
  });
}

export async function uploadProductUpdateImage(
  file: File,
  fileName: string
): Promise<ProductUpdateImageResponse> {
  const res = await uploadProductUpdateImageRequest({
    query: { file_name: fileName },
    body: { file },
    throwOnError: true,
  });
  if (!res.data) {
    throw new Error('Invalid product update image response');
  }
  return res.data;
}

export async function deleteProductUpdateImage(imageId: string): Promise<void> {
  await deleteProductUpdateImageRequest({
    path: { image_id: imageId },
    throwOnError: true,
  });
}

export const PRODUCT_UPDATE_NONE_PERMISSION = '__none__';

export const PRODUCT_UPDATE_NONE_FEATURE_FLAG = '__none__';

export enum ProductUpdatePriority {
  Pinned = 0,
  Default = 1,
  Low = 2,
}

export const PRODUCT_UPDATE_PRIORITY_OPTIONS: readonly {
  value: ProductUpdatePriority;
  label: string;
  description: string;
}[] = [
  {
    value: ProductUpdatePriority.Pinned,
    label: 'Pinned',
    description: 'Shows before default product updates',
  },
  {
    value: ProductUpdatePriority.Default,
    label: 'Default',
    description: 'Standard product update ordering',
  },
  {
    value: ProductUpdatePriority.Low,
    label: 'Low',
    description: 'Shows after default product updates',
  },
];

export const PRODUCT_UPDATE_PRIORITY_SELECT_OPTIONS =
  PRODUCT_UPDATE_PRIORITY_OPTIONS.map(option => ({
    value: String(option.value),
    label: `${option.label} (${option.value})`,
  }));

const PRODUCT_UPDATE_PRIORITY_VALUES = PRODUCT_UPDATE_PRIORITY_OPTIONS.map(
  option => option.value
);

export function isProductUpdatePriority(
  value: number
): value is ProductUpdatePriority {
  return PRODUCT_UPDATE_PRIORITY_VALUES.includes(
    value as ProductUpdatePriority
  );
}

export function productUpdatePriorityFromNumber(
  value: number
): ProductUpdatePriority {
  return isProductUpdatePriority(value) ? value : ProductUpdatePriority.Default;
}

export function productUpdatePriorityDescription(
  priority: ProductUpdatePriority
): string {
  return (
    PRODUCT_UPDATE_PRIORITY_OPTIONS.find(option => option.value === priority)
      ?.description ?? 'Standard product update ordering'
  );
}

export function productUpdatePriorityLabel(
  priority: number | null | undefined
): string {
  const value = priority ?? ProductUpdatePriority.Default;
  const option = PRODUCT_UPDATE_PRIORITY_OPTIONS.find(
    candidate => candidate.value === value
  );
  return option ? `${option.label} (${option.value})` : `Priority ${value}`;
}

export function productUpdateAudienceLabel(
  permissionSlug: string | null | undefined,
  slugToName?: ReadonlyMap<string, string>
): string {
  if (permissionSlug == null) {
    return 'All Users';
  }
  return slugToName?.get(permissionSlug) ?? permissionSlug;
}

export type ProductUpdateTargetLike = {
  id: string;
  permission_slug?: string | null;
  feature_flag_slug?: string | null;
  agency_ids?: string[] | null;
  priority?: number | null;
  expires_at?: string | null;
};

export function productUpdateRowKey(
  update: Pick<ProductUpdateTargetLike, 'id'>
): string {
  return update.id;
}

export function productUpdateTargetLabels(
  update: ProductUpdateTargetLike,
  permissionSlugToName?: ReadonlyMap<string, string>,
  agencyIdToName?: ReadonlyMap<string, string>
): string[] {
  return [
    productUpdateAudienceLabel(update.permission_slug, permissionSlugToName),
    update.feature_flag_slug
      ? `Flag: ${update.feature_flag_slug}`
      : 'All feature flags',
    update.agency_ids?.length
      ? `Agencies: ${update.agency_ids
          .map(id => agencyIdToName?.get(id) ?? id)
          .join(', ')}`
      : 'All agencies',
    `Priority: ${productUpdatePriorityLabel(update.priority)}`,
  ];
}

export function productUpdateTargetLabel(
  update: ProductUpdateTargetLike,
  permissionSlugToName?: ReadonlyMap<string, string>,
  agencyIdToName?: ReadonlyMap<string, string>
): string {
  return productUpdateTargetLabels(
    update,
    permissionSlugToName,
    agencyIdToName
  ).join(' · ');
}
